import { useTranslate } from "ra-core";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";

import { useConfigurationContext } from "../root/ConfigurationContext";
import { useCanvasAnimation } from "./useCanvasAnimation";
import {
  DEFAULT_WELCOME_BACKGROUND,
  WELCOME_BACKGROUNDS,
  type WelcomeBackgroundName,
} from "./welcomeBackgrounds";
import { clearWelcomePending, isWelcomePending } from "./welcomeFlag";

/**
 * Local review only (dev server): http://localhost:5174/?welcome shows the
 * screen without a sign-in and keeps it until a click; ?welcome=aurore (or
 * croissance, constellation) tries another background.
 */
const previewParam = () =>
  import.meta.env.DEV
    ? new URLSearchParams(window.location.search).get("welcome")
    : null;

const isPreview = () => previewParam() !== null;

const backgroundName = (): WelcomeBackgroundName => {
  const name = previewParam();
  return name && name in WELCOME_BACKGROUNDS
    ? (name as WelcomeBackgroundName)
    : DEFAULT_WELCOME_BACKGROUND;
};

/** Time the screen stays before fading into the CRM. */
const DISPLAY_MS = 4000;
const FADE_MS = 700;

/**
 * Full-screen welcome shown once right after signing in: an animated
 * background, the logo and the slogan, then a fade into the dashboard.
 * A click or Escape skips it.
 */
export const WelcomeSplash = () => {
  const translate = useTranslate();
  const { darkModeLogo, title } = useConfigurationContext();
  // Read without clearing, so StrictMode's double render keeps it; cleared below.
  const [isVisible, setVisible] = useState(
    () => isWelcomePending() || isPreview(),
  );
  const [isLeaving, setLeaving] = useState(false);

  useEffect(() => {
    if (!isVisible) return;
    clearWelcomePending();
    const timer = isPreview()
      ? undefined
      : setTimeout(() => setLeaving(true), DISPLAY_MS);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setLeaving(true);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isVisible]);

  useEffect(() => {
    if (!isLeaving) return;
    const timer = setTimeout(() => setVisible(false), FADE_MS);
    return () => clearTimeout(timer);
  }, [isLeaving]);

  if (!isVisible) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={translate("crm.welcome.slogan")}
      onClick={() => setLeaving(true)}
      className={cn(
        "fixed inset-0 z-[100] overflow-hidden bg-header text-header-foreground transition-opacity duration-700 cursor-pointer",
        isLeaving ? "opacity-0" : "opacity-100",
      )}
    >
      <WelcomeBackground />
      <div className="relative flex h-full flex-col items-center justify-center gap-6 px-6 text-center">
        <img
          className="size-20 mix-blend-screen motion-safe:animate-in motion-safe:zoom-in-95 motion-safe:duration-1000"
          src={darkModeLogo}
          alt=""
        />
        <p className="font-heading text-xs uppercase tracking-[0.4em] text-header-foreground/70">
          {title}
        </p>
        <h1 className="font-heading max-w-3xl text-3xl font-light leading-tight md:text-5xl motion-safe:animate-in motion-safe:slide-in-from-bottom-3 motion-safe:duration-1000">
          {translate("crm.welcome.slogan")}
        </h1>
      </div>
    </div>
  );
};

const WelcomeBackground = () => {
  const canvasRef = useCanvasAnimation(WELCOME_BACKGROUNDS[backgroundName()]);
  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="absolute inset-0 size-full"
    />
  );
};
